<template>
    <Head title="Applicant Work History"></Head>
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <Toast />
                <Toolbar class="mb-4">
                    <template v-slot:start>
                        <div class="my-2">
                            <Button
                                label="New"
                                icon="pi pi-plus"
                                class="p-button-success mr-2"
                                @click="openNew"
                            />
                            <Button
                                label="Delete"
                                icon="pi pi-trash"
                                class="p-button-danger"
                                @click="confirmDeleteSelected"
                                :disabled="
                                    !selectedModels || !selectedModels.length
                                "
                            />
                        </div>
                    </template>
                </Toolbar>

                <DataTable
                    ref="dt"
                    :value="models"
                    dataKey="id"
                    v-model:selection="selectedModels"
                    :paginator="true"
                    :rows="10"
                    :loading="loading"
                    :total-records="totalRecords"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    :rowsPerPageOptions="[5, 10, 25]"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} models"
                    responsiveLayout="scroll"
                    :selectAll="selectAll"
                    @row-select="onRowSelect"
                    @row-unselect="onRowUnselect"
                >
                    <template #header>
                        <div
                            class="flex flex-column md:flex-row md:justify-content-between md:align-items-center"
                        >
                            <h5 class="m-0">Manage Work History</h5>
                            <span class="block mt-2 md:mt-0 p-input-icon-left">
                                <i class="pi pi-search" />
                                <InputText
                                    v-model="filters['global'].value"
                                    placeholder="Search..."
                                />
                            </span>
                        </div>
                    </template>

                    <Column
                        selectionMode="multiple"
                        headerStyle="width: 3rem"
                    ></Column>
                    <Column
                        field="code"
                        header="Code"
                        :sortable="true"
                        headerStyle="width:5%; min-width:10rem;"
                    >
                        <template #body="slotProps">
                            <span class="p-column-title">Id</span>
                            {{ slotProps.data.id }}
                        </template>
                    </Column>
                    <Column
                        field="position"
                        header="Position"
                        :sortable="true"
                        headerStyle="width:70%; min-width:10rem;"
                    >
                        <template #body="slotProps">
                            <span class="p-column-title">Position</span>
                            {{ slotProps.data.position }}
                        </template>
                    </Column>
                    <Column
                        field="company_name"
                        header="Company Name"
                        :sortable="true"
                        headerStyle="width:70%; min-width:10rem;"
                    >
                        <template #body="slotProps">
                            <span class="p-column-title">Company Name</span>
                            {{ slotProps.data.company_name }}
                        </template>
                    </Column>
                    <Column
                        field="start_date"
                        header="Start Date"
                        :sortable="true"
                        headerStyle="width:70%; min-width:10rem;"
                    >
                        <template #body="slotProps">
                            <span class="p-column-title">Start Date</span>
                            {{ slotProps.data.start_date }}
                        </template>
                    </Column>
                    <Column
                        field="end_date"
                        header="End Date"
                        :sortable="true"
                        headerStyle="width:70%; min-width:10rem;"
                    >
                        <template #body="slotProps">
                            <span class="p-column-title">End Date</span>
                            {{ slotProps.data.end_date }}
                        </template>
                    </Column>
                    <Column headerStyle="width:15%;min-width:10rem;">
                        <template #body="slotProps">
                            <Button
                                icon="pi pi-pencil"
                                class="p-button-rounded p-button-success mr-2"
                                @click="editModel(slotProps.data)"
                            />
                            <Button
                                icon="pi pi-trash"
                                class="p-button-rounded p-button-warning mt-2"
                                @click="confirmDeleteModel(slotProps.data)"
                            />
                        </template>
                    </Column>
                </DataTable>

                <Dialog
                    v-model:visible="modelDialog"
                    :style="{ width: '450px' }"
                    header="Formal Education Detail"
                    :modal="true"
                    class="p-fluid"
                >
                <div class="field">
                        <label for="position">Position name</label>
                        <InputText
                            id="position"
                            v-model.trim="model.position"
                            required="true"
                            autofocus
                            :class="{ 'p-invalid': submitted && !model.position }"
                        />
                        <small class="p-invalid" v-if="submitted && !model.position"
                            >Position is required.</small
                        >
                    </div>
                    <div class="field">
                        <label for="company_name">Company name</label>
                        <InputText
                            id="company_name"
                            v-model.trim="model.company_name"
                            required="true"
                            autofocus
                            :class="{ 'p-invalid': submitted && !model.company_name }"
                        />
                        <small class="p-invalid" v-if="submitted && !model.company_name"
                            >Company name is required.</small
                        >
                    </div>
                    <div class="field">
                        <label for="start_date">Start Date</label>
                        <Calendar :showIcon="true"
                            :showButtonBar="true"
                            v-model="model.start_date"
                            :class="{'p-invalid' : submitted && !model.start_date}"
                            dateFormat="yy-mm-dd">
                        </Calendar>
                        <small class="p-invalid" v-if="submitted && !model.start_date"
                            >Start date is required.</small
                        >
                    </div>
                    <div class="field">
                        <label for="start_date">End Date</label>
                        <Calendar :showIcon="true"
                            :showButtonBar="true"
                            v-model="model.end_date"
                            :class="{'p-invalid' : submitted && !model.end_date}"
                            dateFormat="yy-mm-dd">
                        </Calendar>
                        <small class="p-invalid" v-if="submitted && !model.end_date"
                            >End date is required.</small
                        >
                    </div>
                    <div class="field">
                        <label for="start_date">Reason To Leave</label>
                        <TextArea
                            id="reason_to_leave"
                            name="reason_to_leave"
                            v-model="model.reason_to_leave"
                            required="true"
                            rows="5"
                            cols="50"
                            :class="{'p-invalid' : submitted && !model.reason_to_leave}"
                        >
                        </TextArea>
                        <small class="p-invalid" v-if="submitted && !model.reason_to_leave"
                            >Reason To Leave is required.</small
                        >
                    </div>
                    <template #footer>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            class="p-button-text"
                            @click="hideDialog"
                        />
                        <Button
                            label="Save"
                            icon="pi pi-check"
                            class="p-button-text"
                            @click="saveModel"
                        />
                    </template>
                </Dialog>

                <Dialog
                    v-model:visible="deleteModelDialog"
                    :style="{ width: '450px' }"
                    header="Confirm"
                    :modal="true"
                >
                    <div class="flex align-items-center justify-content-center">
                        <i
                            class="pi pi-exclamation-triangle mr-3"
                            style="font-size: 2rem"
                        />
                        <span v-if="model"
                            >Are you sure you want to delete
                            <b>{{ model.name }}</b
                            >?</span
                        >
                    </div>
                    <template #footer>
                        <Button
                            label="No"
                            icon="pi pi-times"
                            class="p-button-text"
                            @click="deleteModelDialog = false"
                        />
                        <Button
                            label="Yes"
                            icon="pi pi-check"
                            class="p-button-text"
                            @click="deleteModel"
                        />
                    </template>
                </Dialog>

                <Dialog
                    v-model:visible="deleteModelsDialog"
                    :style="{ width: '450px' }"
                    header="Confirm"
                    :modal="true"
                >
                    <div class="flex align-items-center justify-content-center">
                        <i
                            class="pi pi-exclamation-triangle mr-3"
                            style="font-size: 2rem"
                        />
                        <span v-if="model"
                            >Are you sure you want to delete the selected
                            models?</span
                        >
                    </div>
                    <template #footer>
                        <Button
                            label="No"
                            icon="pi pi-times"
                            class="p-button-text"
                            @click="deleteModelsDialog = false"
                        />
                        <Button
                            label="Yes"
                            icon="pi pi-check"
                            class="p-button-text"
                            @click="deleteSelectedModels"
                        />
                    </template>
                </Dialog>
            </div>
        </div>
    </div>
</template>
<script>
import axios from "axios";
import { Head } from "@inertiajs/inertia-vue3";
import { FilterMatchMode } from "primevue/api";
import DataTable from "primevue/datatable";
import Calendar from 'primevue/calendar';
import Column from "primevue/column";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import TextArea from "primevue/textarea";
import Toolbar from "primevue/toolbar";
import Toast from "primevue/toast";
import InputText from "primevue/inputtext";

export default {
    components: {
        Head,
        DataTable,
        Calendar,
        Column,
        Dialog,
        Button,
        TextArea,
        Toolbar,
        Toast,
        InputText,
    },
    data() {
        return {
            applicant_id: this.$page.props.candidate.data.id,
            searchFilter: "",
            loading: true,
            totalRecords: 0,
            selectedModel: null,
            filters: null,
            model: {},
            modelDialog: false,
            models: this.$page.props.candidate.data.work_histories,
            deleteModelDialog: false,
            deleteModelsDialog: false,
            selectedModels: null,
            submitted: false,
            selectAll: false,
        };
    },
    created() {
        this.initFilters();
    },
    mounted() {
        this.loading = false;
    },
    methods: {
        onRowSelect() {
            this.selectAll = this.selectedModels.length === this.totalRecord;
        },
        onRowUnselect() {
            this.selectAll = false;
        },
        openNew() {
            this.model = {};
            this.submitted = false;
            this.modelDialog = true;
        },
        hideDialog() {
            this.modelDialog = false;
            this.submitted = false;
        },
        saveModel() {
            this.submitted = true;
            this.model.applicant_id = this.applicant_id;
            if (this.model) {
                if (this.model.id) {
                    this.models[this.findIndexById(this.model.id)] = this.model;
                    axios
                        .put(this.route('applicant-work-histories.update',this.model.id), this.model)
                        .then((response) => {
                            this.$toast.add({
                                severity: "success",
                                summary: "Successful",
                                detail: "Formal Education Updated",
                                life: 3000,
                            });
                        })
                        .catch((error) => {
                            this.$toast.add({
                                severity: "error",
                                summary: "Error",
                                detail: "Formal Education not updated",
                                life: 3000,
                            });
                        });
                }
                // If empty then add new record
                else {
                    axios
                        .post(this.route('applicant-work-histories.store'), this.model)
                        .then((response) => {
                            this.model = response.data.data;
                            this.models.unshift(this.model);
                            this.$toast.add({
                                severity: "success",
                                summary: "Successful",
                                detail: "Formal Education Created",
                                life: 3000,
                            });
                        })
                        .catch((error) => {
                            this.$toast.add({
                                severity: "error",
                                summary: "Error",
                                detail: "Formal Education not created",
                                life: 3000,
                            });
                        });
                }
                this.modelDialog = false;
                this.model = {};
            }
        },
        editModel(model) {
            this.model = { ...model };
            this.modelDialog = true;
        },
        confirmDeleteModel(model) {
            this.model = model;
            this.deleteModelDialog = true;
        },
        deleteModel() {
            axios
                .delete(this.route('applicant-work-histories.destroy',this.model.id))
                .then((response) => {
                    this.$toast.add({
                        severity: "success",
                        summary: "Successful",
                        detail: "Formal Education Deleted",
                        life: 3000,
                    });
                    this.models = this.models.filter(val => val.id !== this.model.id);
                    this.deleteModelDialog = false;
                    this.model = {};
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: "error",
                        summary: "Error",
                        detail: "Formal Education not deleted",
                        life: 3000,
                    });
                });
        },
        confirmDeleteSelected() {
            this.deleteModelsDialog = true;
        },
        deleteSelectedModels() {
            this.models = this.models.filter(
                (val) => !this.selectedModels.includes(val)
            );
            this.deleteModelsDialog = false;
            this.selectedModels = null;
            this.$toast.add({
                severity: "success",
                summary: "Successful",
                detail: "Models Deleted",
                life: 3000,
            });
        },
        findIndexById(id) {
			let index = -1;
			for (let i = 0; i < this.models.length; i++) {
				if (this.models[i].id === id) {
					index = i;
					break;
				}
			}
			return index;
		},
        initFilters(){
            this.filters = {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            };
        },
    },
};
</script>
